import { AlertManager, useAlerts } from "client/Hooks";
import { isNil, isNotNil } from "client/Misc/utils";
import { useEffect, useState } from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	IFolderViewModel,
	LazyLoadedChildren,
	listFolders
} from "client/Api/Email/email";
import { ObjectWithClientId, setClientId } from "client/Misc/clientId";

export type SelectedFolder = {
	id: string | undefined;
	name: string;
	smtpAddress: string | null;
};

export const useFolderChildren = (
	currentFolder: SelectedFolder
): [isBusy: boolean, items: readonly ObjectWithClientId<SelectedFolder>[]] => {
	const [items, setItems] = useState<readonly ObjectWithClientId<SelectedFolder>[]>([]);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const alertManager: AlertManager = useAlerts();

	useEffect(
		() => {
			const loadFolderContent = async (
				currentFolder: SelectedFolder | undefined,
				signal: AbortSignal
			): Promise<void> => {
				if (isNil(currentFolder)) {
					return;
				}

				try {
					setIsBusy(true);

					const { smtpAddress, id } = currentFolder;
					const children: readonly IFolderViewModel<LazyLoadedChildren>[] = await listFolders(
						{
							folderId: isNotNil(id)
								? {
									isSet: true,
									value: id
								}
								: { isSet: false },
							smtpAddress: isNotNil(smtpAddress)
								? {
									isSet: true,
									value: smtpAddress
								}
								: { isSet: false }
						},
						signal
					);

					setItems(
						children.map((x): ObjectWithClientId<SelectedFolder> => setClientId({
							id: x.folderId.id,
							name: x.name,
							smtpAddress
						}))
					);

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
					setItems([]);
				}
			};

			const cancellation = new AbortController();
			loadFolderContent(currentFolder, cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, currentFolder]
	);

	return [isBusy, items];
};